import React, { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";

import { useAutoTableHeight } from "../../useAutoTableHeight";
import { formatNumber, isNegative } from "../utils/reportUtils";
import CashFlowRow from "./CashFlowRow";

export default function CashFlowTable({ data, texts }) {
  const isRTL = texts.en === "Arabic";
  const [expandedRows, setExpandedRows] = useState(new Set());
  const tableHeight = useAutoTableHeight(200);

  if (!Array.isArray(data?.headers) || !Array.isArray(data?.rows)) {
    return (
      <Flex justify="center" align="center" h="100px">
        <Text fontSize="sm">Loading report data...</Text>
      </Flex>
    );
  }

  const toggleRow = (rowId) => {
    setExpandedRows((previous) => {
      const next = new Set(previous);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const rootRows = data.rows.filter(
    (row) => !row.parentId && row.type !== "grand-total"
  );
  const grandTotalRows = data.rows.filter((row) => row.type === "grand-total");

  return (
    <Box
      bg="white"
      borderRadius="md"
      boxShadow="sm"
      border="1px solid"
      borderColor="#eaeef2"
      height={`${tableHeight}px`}
      position="relative"
    >
      <Box position="absolute" inset="0" overflowY="auto" overflowX="auto">
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            direction: isRTL ? "rtl" : "ltr",
            fontSize: "11px",
          }}
        >
          <thead>
            <tr
              style={{
                position: "sticky",
                top: 0,
                zIndex: 5,
                background: "#f5f7fa",
                borderBottom: "1px solid #dce1e6",
              }}
            >
              {data.headers.map((header, index) => (
                <th
                  key={header}
                  style={{
                    padding: "2px 3px",
                    textAlign:
                      index === 0
                        ? isRTL
                          ? "right"
                          : "left"
                        : isRTL
                        ? "left"
                        : "right",
                    minWidth: index === 0 ? "300px" : "80px",
                    fontSize: "11px",
                    fontWeight: "500",
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rootRows.map((row) => (
              <CashFlowRow
                key={row.id}
                row={row}
                data={data}
                expandedRows={expandedRows}
                onToggle={toggleRow}
                isRTL={isRTL}
              />
            ))}
            {grandTotalRows.map((row) => (
              <tr key={row.id}>
                {data.headers.map((header, index) => {
                  const value = row.values[index];
                  const formattedValue = index > 0 ? formatNumber(value) : value;
                  const negative = index > 0 && isNegative(value);

                  return (
                    <td
                      key={`${row.id}-${header}`}
                      style={{
                        padding: "2px 3px",
                        fontSize: "11px",
                        fontWeight: "600",
                        textAlign:
                          index === 0
                            ? isRTL
                              ? "right"
                              : "left"
                            : isRTL
                            ? "left"
                            : "right",
                        color: negative ? "#dc3545" : "#333333",
                      }}
                    >
                      {formattedValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}
